"use client"
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { useParams } from "next/navigation"
import type { PublicConversationPointer, PublicConversationSession } from "@/modules/chatbot/lifecycle-contracts"
import { communicationActions as actions, communicationsStore } from "./state"
import { ApiError } from "@/lib/api-client"

const storageKey = "danaro.public-conversation.v1"
const resetEvent = "danaro-public-conversation-reset"
function readPointer(): PublicConversationPointer | null {
  try {
    const value = JSON.parse(sessionStorage.getItem(storageKey) ?? "null")
    return value && typeof value.id === "string" && /^[a-f0-9-]{36}$/i.test(value.id) && typeof value.actor === "string" && typeof value.autoResumeUntil === "string" ? value : null
  } catch { return null }
}
function writePointer(value: PublicConversationPointer | null) {
  try { if (value) sessionStorage.setItem(storageKey, JSON.stringify(value)); else sessionStorage.removeItem(storageKey) } catch { /* The in-memory selection still works when storage is blocked. */ }
}
export function useConversationSpanish() { return useParams<{ locale?: string }>()?.locale === "es" }

export function usePublicConversation(enabled: boolean, open: boolean, viewer: string, status?: string) {
  const es = useConversationSpanish()
  const [phase, setPhase] = useState<"welcome" | "loading" | "new" | "active" | "expired">("welcome")
  const [activeId, setActiveId] = useState<string | null>(null)
  const [metadata, setMetadata] = useState<PublicConversationSession | null>(null)
  const [error, setError] = useState(""), [busy, setBusy] = useState(false), [cycle, setCycle] = useState(0)
  const [draft, setDraft] = useState("")
  const [recheck, setRecheck] = useState(0)
  const [tabReady, setTabReady] = useState(false)
  const [canClaim, setCanClaim] = useState(false)
  const operation = useRef(0), dirty = useRef(false), active = useRef<string | null>(null), pointer = useRef<PublicConversationPointer | null>(null)
  const draftOpen = useRef(false)
  const pendingSend = useRef<number | null>(null)
  const channel = useRef<BroadcastChannel | null>(null)
  const draftOwner = useRef(viewer)
  const draftScope = useRef<{ viewer: string; cycle: number } | null>({ viewer, cycle })
  useLayoutEffect(() => { draftScope.current = { viewer, cycle } }, [viewer, cycle])
  useLayoutEffect(() => { active.current = activeId }, [activeId])
  useLayoutEffect(() => {
    if (!enabled || draftOwner.current===viewer) return
    const previous = draftOwner.current
    draftOwner.current=viewer
    // The first anonymous-to-guest bootstrap is part of that visitor's send.
    // Other identity transitions discard drafts/media even without a thread ID.
    if (previous==="unknown" && viewer.startsWith("guest:") && !pointer.current) return
    pendingSend.current = null
    operation.current++;draftScope.current=null;dirty.current=false;draftOpen.current=false;active.current=null
    actions.clearPublicSelection();setActiveId(null);setDraft("");setMetadata(null);setCanClaim(false);setPhase("welcome");setBusy(false);setCycle(value=>value+1)
  },[enabled,viewer])
  const activate = useCallback((id: string, data: PublicConversationSession) => {
    draftOpen.current = false
    const until = data.current?.autoResumeUntil ?? data.expiresAt ?? new Date(Date.now() + data.autoResumeMinutes * 60000).toISOString()
    const value = { id, actor: data.actor ?? viewer, autoResumeUntil: until }
    writePointer(value); pointer.current = value; active.current = id
    setMetadata(data); setCanClaim(false); setActiveId(id); setPhase("active"); setError("")
  }, [viewer])
  useEffect(() => {
    if (!enabled) return
    // Duplicating a tab can copy sessionStorage. Claim its tab key before reading
    // any transcript; a second live tab starts at the welcome screen.
    const nonce = crypto.randomUUID()
    let tabKey = nonce
    try { tabKey = sessionStorage.getItem(storageKey+".tab") || nonce; sessionStorage.setItem(storageKey+".tab",tabKey) } catch {}
    const claims = typeof BroadcastChannel!=="undefined" ? new BroadcastChannel("danaro-public-tab-claims") : null
    const duplicated = () => { operation.current++;pendingSend.current=null;draftScope.current=null;draftOpen.current=false;writePointer(null);pointer.current=null;setCanClaim(false);setActiveId(null);setPhase("welcome") }
    if (claims) {
      claims.onmessage = event => {
        if (event.data?.tabKey!==tabKey || event.data?.nonce===nonce) return
        if (event.data?.type==="claim") claims.postMessage({type:"in-use",tabKey,nonce,target:event.data.nonce})
        if (event.data?.type==="in-use" && event.data.target===nonce) { duplicated();tabKey=nonce;try{sessionStorage.setItem(storageKey+".tab",nonce)}catch{} }
      }
      claims.postMessage({type:"claim",tabKey,nonce})
    } else if (window.opener && (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming|undefined)?.type!=="reload") duplicated()
    const ready = setTimeout(()=>setTabReady(true),150)
    const reset = () => {
      pendingSend.current = null
      operation.current++; draftScope.current = null; writePointer(null); pointer.current = null; active.current = null; dirty.current = false; draftOpen.current = false
      actions.reset(); setActiveId(null); setDraft(""); setMetadata(null); setCanClaim(false); setPhase("welcome"); setCycle(value => value + 1); setError("");setBusy(false)
    }
    const storage = (event: StorageEvent) => { if (event.key === resetEvent && event.newValue) reset() }
    if (typeof BroadcastChannel !== "undefined") {
      channel.current = new BroadcastChannel(resetEvent)
      channel.current.onmessage = event => { if (event.data?.type === "forget") reset() }
    }
    window.addEventListener(resetEvent, reset); window.addEventListener("storage", storage)
    return () => { clearTimeout(ready);claims?.close();channel.current?.close(); channel.current = null; window.removeEventListener(resetEvent, reset); window.removeEventListener("storage", storage) }
  }, [enabled])
  useEffect(() => {
    if (!enabled || !open || !tabReady || status === "loading" || pendingSend.current !== null) return
    const current = ++operation.current
    let cancelled = false
    pointer.current = readPointer()
    const saved = pointer.current
    // An explicitly opened draft belongs to this visitor even before the first
    // keystroke. Revalidation must not unmount its composer and discard media.
    if (!dirty.current && !draftOpen.current) setPhase("loading")
    void actions.publicSession(saved?.id).then(async data => {
      if (cancelled || current !== operation.current) return
      setMetadata(data)
      setCanClaim(!!data.claimable && !!saved?.actor.startsWith("guest:") && saved.actor !== data.actor)
      if ((dirty.current || draftOpen.current) && (draftOwner.current===data.actor || draftOwner.current==="unknown" && !data.actor)) { setPhase(active.current ? "active" : "new"); return }
      if (dirty.current || draftOpen.current) { draftScope.current=null;dirty.current=false;draftOpen.current=false;setDraft("");setCycle(value=>value+1) }
      if (saved && saved.actor!==data.actor && !data.claimable) {
        draftScope.current=null;dirty.current=false;active.current=null;setActiveId(null);setDraft("");setCycle(value=>value+1)
        writePointer(null);pointer.current=null
      }
      if (saved && saved.actor === data.actor && data.current?.canAutoResume) {
        const resumed = await actions.continuePublic(saved.id, true)
        if (!cancelled && current === operation.current) activate(saved.id, resumed)
      } else {
        setActiveId(null); actions.clearPublicSelection(); setPhase(saved && !data.actor ? "expired" : "welcome")
      }
      setError(data.schemaReady ? "" : es ? "Estamos actualizando el chat. Puedes contactar a nuestro equipo." : "Chat is being updated. You can still contact our team.")
    }).catch(issue => {
      if (cancelled || current !== operation.current || issue instanceof DOMException && issue.name === "AbortError") return
      if (dirty.current || draftOpen.current) setPhase(active.current ? "active" : "new")
      else { setActiveId(null); setPhase(issue instanceof ApiError && issue.status === 401 ? "expired" : "welcome") }
      setError(issue instanceof Error ? issue.message : es ? "No se pudo abrir el chat." : "The conversation could not open.")
    })
    return () => { cancelled = true }
  }, [enabled, open, viewer, status, activate, es, recheck, tabReady])
  const metadataActor=metadata?.actor,autoResumeUntil=metadata?.current?.autoResumeUntil
  useEffect(() => {
    if (!enabled || !open) return
    const focus = () => setRecheck(value => value + 1)
    window.addEventListener("focus", focus)
    const until = metadataActor?.startsWith("guest:") ? autoResumeUntil : null
    const timeout = until && Date.parse(until) > Date.now() ? setTimeout(focus, Math.min(2147483647, Date.parse(until) - Date.now() + 100)) : undefined
    return () => { window.removeEventListener("focus", focus); clearTimeout(timeout) }
  }, [enabled, open, metadataActor, autoResumeUntil])
  useEffect(() => {
    if (!enabled) return
    const unload = (event: BeforeUnloadEvent) => { if (dirty.current) { event.preventDefault(); event.returnValue = "" } }
    window.addEventListener("beforeunload", unload)
    return () => window.removeEventListener("beforeunload", unload)
  }, [enabled])
  const startNew = useCallback(() => {
    pendingSend.current = null
    operation.current++; draftScope.current = null; pointer.current = null; writePointer(null); active.current = null; dirty.current = false; draftOpen.current = true
    actions.clearPublicSelection(); setActiveId(null); setDraft(""); setCanClaim(false); setPhase("new"); setError(""); setBusy(false);setCycle(value => value + 1)
  }, [])
  const beginSend = useCallback(() => {
    // Guest bootstrap changes viewer/status before the first message is posted.
    // Fence older session reads and keep new reads from replacing that draft.
    const token = ++operation.current
    pendingSend.current = token
    return () => { if (pendingSend.current === token) pendingSend.current = null }
  }, [])
  const resume = useCallback(async () => {
    const id = metadata?.recent?.id
    if (!id || busy) return
    const current = ++operation.current
    setBusy(true); setError("")
    try { const data = await actions.continuePublic(id); if (current === operation.current) activate(id, data) }
    catch (issue) { if (current === operation.current) setError(issue instanceof Error ? issue.message : "The conversation could not continue.") }
    finally { setBusy(false) }
  }, [metadata, busy, activate])
  const forget = useCallback(async () => {
    if (busy) return
    setBusy(true); setError("")
    try {
      await actions.forgetPublic(); channel.current?.postMessage({ type: "forget" })
      try { localStorage.setItem(resetEvent, crypto.randomUUID()); localStorage.removeItem(resetEvent) } catch { /* BroadcastChannel or the server will revoke other tabs. */ }
      window.dispatchEvent(new Event(resetEvent))
    } catch (issue) { setError(issue instanceof Error ? issue.message : "Device access could not be cleared.") }
    finally { setBusy(false) }
  }, [busy])
  const claim = useCallback(async () => {
    const saved = pointer.current ?? readPointer()
    if (!saved || busy) return
    setBusy(true); setError("")
    try { const data = await actions.claimPublic(saved.id); activate(saved.id, data) }
    catch (issue) { setError(issue instanceof Error ? issue.message : "The conversation could not be saved to your account.") }
    finally { setBusy(false) }
  }, [busy, activate])
  const sent = useCallback(async (id: string) => {
    const current = ++operation.current
    // A successful send remains successful even if refreshing resume metadata fails.
    dirty.current = false; draftOpen.current = false; setDraft(""); setCanClaim(false); active.current = id; setActiveId(id); setPhase("active")
    const fallback: PublicConversationPointer = { id, actor: communicationsStore.getSnapshot().viewer, autoResumeUntil: new Date(Date.now() + (metadata?.autoResumeMinutes ?? 30) * 60000).toISOString() }
    pointer.current = fallback; writePointer(fallback)
    try { const data = await actions.publicSession(id); if (current === operation.current) activate(id, data) } catch { /* The next open rechecks server access. */ }
  }, [metadata?.autoResumeMinutes, activate])
  const dirtyChanged = useCallback((value: boolean) => {
    // Animated outgoing composers can notify after another actor/draft takes over.
    if (draftScope.current?.viewer === viewer && draftScope.current.cycle === cycle) dirty.current = value
  }, [viewer, cycle])
  const prepareOpen = () => { if (!dirty.current && !draftOpen.current) setPhase("loading") }
  return { enabled, es, phase, activeId, metadata, error, busy, cycle, draft, setDraft, startNew, beginSend, resume, forget, claim, sent, dirtyChanged, prepareOpen, canClaim, isDirty: () => dirty.current }
}
